import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, message } from 'antd';
import UnifiedCascadingSelect from '../UnifiedCascadingSelect';
import { DoseInput, DeleteRowButton, ProductLabelButton } from './components';
import {
  isRowComplete,
  isRowEmpty,
  filterValidRows,
  sanitizeDoseInput,
} from '../../utils/validators';
import { buildEnrichedProduct } from '../../utils/transformers';

/**
 * Step 4 - Commercial Products Component
 * Multi-row dynamic form for product selection with auto-add functionality
 * Matches Figma design exactly - NO TABLE, custom row layout
 *
 * Features:
 * - Auto-add empty row when current row is complete
 * - Label icon to view product details
 * - Delete icon per row
 * - Filters out empty rows before sending to API
 * - URL param support with security validation
 * - Comma/dot number handling
 *
 * @param {Object} props
 * @param {Array} props.selectedProducts - Array of {id, name, dose, unit} objects
 * @param {Function} props.onSelectProducts - Callback when products are updated
 * @param {Object} props.selections - Current selections from all steps
 * @param {Object} [props.enrichedData] - Enriched data with cached product/unit labels
 * @param {Function} [props.onMismatchChange] - Callback when mismatch status changes
 */
const Step4CommercialProducts = React.memo(
  ({
    selectedProducts = [],
    onSelectProducts,
    selections = {},
    enrichedData = {},
    onMismatchChange,
  }) => {
    const { t } = useTranslation('riskCalculator');

    // Memoize cascading select props to prevent unnecessary re-fetches
    const cascadingProps = useMemo(
      () => ({
        cultures: selections.cultures || [],
        treatmentType: selections.treatmentType || null,
        applicationTechnique: selections.applicationTechnique || null,
      }),
      [
        selections.cultures,
        selections.treatmentType,
        selections.applicationTechnique,
      ]
    );

    // Internal state for rows (includes empty row for adding)
    const [rows, setRows] = useState([]);

    // Track mismatches from all product/unit selects
    const [selectMismatches, setSelectMismatches] = useState({});

    // Create mismatch handler for a specific row and type (product or unit)
    const createMismatchHandler = useCallback((rowIndex, selectType) => {
      return (hasMismatch, message) => {
        const key = `${rowIndex}-${selectType}`;
        setSelectMismatches((prev) => {
          const newState = { ...prev };
          if (hasMismatch) {
            newState[key] = message;
          } else {
            delete newState[key];
          }
          return newState;
        });
      };
    }, []);

    // Report aggregated mismatch status to parent
    useEffect(() => {
      if (!onMismatchChange) return;
      const hasMismatch = Object.keys(selectMismatches).length > 0;
      const message = hasMismatch ? Object.values(selectMismatches)[0] : null;
      onMismatchChange(hasMismatch, message);
    }, [selectMismatches, onMismatchChange]);

    /**
     * Initialize rows from selectedProducts + enrich with data from enrichedData
     * Always ensure one empty row at end
     */
    useEffect(() => {
      const validProducts = selectedProducts.filter((p) => p.id && p.id !== '');

      // Merge enrichedData into products (for cold start URL restoration)
      const enrichedProducts = validProducts.map((product) => {
        const enriched = enrichedData.products?.find(
          (ep) => ep.id === product.id
        );
        // Get sageDoses from: enriched.sageDoses (array) OR convert listedDosages (string) OR product.sageDoses OR empty
        let sageDoses = [];
        if (enriched?.sageDoses?.length > 0) {
          sageDoses = enriched.sageDoses;
        } else if (enriched?.listedDosages) {
          sageDoses = enriched.listedDosages.split(' • ');
        } else if (product.sageDoses?.length > 0) {
          sageDoses = product.sageDoses;
        }
        return {
          ...product,
          name: enriched?.name || product.name || '',
          unitName: enriched?.unitName || product.unitName || '',
          sageDoses,
        };
      });

      // Always have at least one empty row
      if (enrichedProducts.length === 0) {
        setRows([createEmptyRow()]);
      } else {
        // Add empty row at end if last row is not empty
        const lastRow = enrichedProducts[enrichedProducts.length - 1];
        if (lastRow && lastRow.id) {
          setRows([...enrichedProducts, createEmptyRow()]);
        } else {
          setRows(enrichedProducts);
        }
      }
    }, [selectedProducts, enrichedData.products]);

    /**
     * Create an empty row
     */
    function createEmptyRow() {
      return {
        id: '',
        name: '',
        dose: '',
        unit: '',
        unitName: '',
        sageDoses: [], // Treatment doses from SAgE
        // Temporary ID for React key
        _tempId: `temp_${Date.now()}_${Math.random()}`,
      };
    }

    /**
     * Update rows and sync with parent (filtering out empty rows)
     */
    function updateRows(newRows) {
      setRows(newRows);

      // Filter out empty rows before sending to parent
      const validRows = filterValidRows(newRows);

      // Build enriched products with unit names and approval numbers using shared utility
      const enrichedProducts = validRows.map(buildEnrichedProduct);

      onSelectProducts(validRows, enrichedProducts);
    }

    /**
     * Handle product selection in a row
     */
    const handleProductChange = useCallback(
      (rowIndex, value, option) => {
        const newRows = [...rows];
        const row = newRows[rowIndex];

        if (!value) {
          // Clear product
          row.id = '';
          row.name = '';
          row.approvalNumber = '';
          row.sageDoses = [];
        } else {
          // Check if product is already added in another row
          const isDuplicate = newRows.some(
            (r, idx) => idx !== rowIndex && r.id === value && r.id !== ''
          );

          if (isDuplicate) {
            // Show warning message
            message.warning(t('products_duplicate_warning'));
            // Don't add the duplicate, clear the current row
            row.id = '';
            row.name = '';
            row.approvalNumber = '';
            row.sageDoses = [];
            return;
          }

          row.id = value;
          // Use the original name from option.data.name or option.name
          row.name = option?.data?.name || option?.name || value;
          row.approvalNumber =
            option?.data?.approval_number || option?.approval_number || '';
          row.sageDoses = option?.data?.doses || option?.doses || []; // Store SAgE doses
          // Keep existing unit if set, otherwise it will be empty
        }

        // Check if row is now complete and add new empty row if needed
        if (isRowComplete(row)) {
          // Check if last row - if so, add new empty row
          if (rowIndex === newRows.length - 1) {
            newRows.push(createEmptyRow());
          }
        }

        // Build enriched products array using shared utility
        const validRows = filterValidRows(newRows);
        const enrichedProducts = validRows.map(buildEnrichedProduct);

        // Update rows locally
        setRows(newRows);
        // Send enriched data to parent
        onSelectProducts(validRows, enrichedProducts);
      },
      [rows, onSelectProducts, t]
    );

    /**
     * Handle dose change with comma/dot support
     */
    const handleDoseChange = useCallback(
      (rowIndex, inputValue) => {
        const newRows = [...rows];
        const row = newRows[rowIndex];

        // Sanitize input using shared utility
        const { storedValue } = sanitizeDoseInput(inputValue);
        row.dose = storedValue;

        // Check if row is now complete and add new empty row if needed
        if (isRowComplete(row)) {
          if (rowIndex === newRows.length - 1) {
            newRows.push(createEmptyRow());
          }
        }

        updateRows(newRows);
      },
      [rows]
    );

    /**
     * Handle unit change
     */
    const handleUnitChange = useCallback(
      (rowIndex, value, option) => {
        const newRows = [...rows];
        const row = newRows[rowIndex];
        row.unit = value;
        row.unitName = option?.label || option?.data?.name || '';

        // Check if row is now complete and add new empty row if needed
        if (isRowComplete(row)) {
          if (rowIndex === newRows.length - 1) {
            newRows.push(createEmptyRow());
          }
        }

        // Build enriched products using shared utility
        const validRows = filterValidRows(newRows);
        const enrichedProducts = validRows.map(buildEnrichedProduct);

        setRows(newRows);
        onSelectProducts(validRows, enrichedProducts);
      },
      [rows, onSelectProducts]
    );

    /**
     * Handle row deletion
     */
    const handleDeleteRow = useCallback(
      (rowIndex) => {
        const newRows = [...rows];
        newRows.splice(rowIndex, 1);

        // Always keep at least one row (empty)
        if (newRows.length === 0) {
          newRows.push(createEmptyRow());
        }

        updateRows(newRows);
      },
      [rows]
    );

    return (
      <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-full mx-auto px-0 sm:px-4">
        {/* Title */}
        <h3 className="text-xl sm:text-2xl lg:text-3xl font-medium text-center normal-case">
          {t('products_title')}
        </h3>

        {/* Description */}
        <p className="text-xs sm:text-sm text-[#595959]">
          {t('products_description')}
        </p>

        {/* Note */}
        <p className="text-xs sm:text-sm text-[#595959] italic">
          {t('products_note')}
        </p>

        {/* Products Table-like Layout */}
        <div className="w-full flex flex-col justify-end items-center p-3 sm:p-6 gap-4 sm:gap-8 self-stretch rounded-2xl bg-[#FAFAFA]">
          {/* Header Row - Hidden on mobile */}
          <div className="hidden w-full lg:grid lg:grid-cols-[minmax(200px,2fr)_80px_minmax(140px,2fr)_140px_140px_60px] gap-2 items-end rounded-lg font-semibold text-sm mb-2">
            <div className="truncate">{t('products_column_product')}</div>
            <div className="text-center">{t('products_column_label')}</div>
            <div className="truncate">{t('products_column_dosages')}</div>
            <div className="truncate">{t('products_column_dose')}</div>
            <div className="truncate">{t('products_column_unit')}</div>
            <div></div>
          </div>

          {/* Product Rows */}
          {rows.map((row, index) => (
            <React.Fragment key={row._tempId || row.id || index}>
              {/* Line separator between products on mobile (not before first row) */}
              {index > 0 && (
                <div className="w-full h-px bg-gray-200 lg:hidden" />
              )}

              <div className="w-full">
                {/* Mobile Layout - Vertical Stack - Each input on its own line */}
                <div className="flex flex-col gap-3 lg:hidden">
                  {/* Product Select */}
                  <div className="w-full">
                    <label className="block text-xs font-medium text-[#595959] mb-1">
                      {t('products_column_product')}
                    </label>
                    <UnifiedCascadingSelect
                      type="products"
                      cultures={cascadingProps.cultures}
                      treatmentType={cascadingProps.treatmentType}
                      applicationTechnique={cascadingProps.applicationTechnique}
                      value={row.id || null}
                      onChange={(value, option) =>
                        handleProductChange(index, value, option)
                      }
                      placeholder={t('products_search_placeholder')}
                      size="large"
                      enrichedLabel={row.name}
                      onMismatchChange={createMismatchHandler(index, 'product')}
                      allowShowAll
                    />
                  </div>

                  {/* Label Button - on its own line */}
                  {row.id && (
                    <div className="w-full">
                      <label className="block text-xs font-medium text-[#595959] mb-1">
                        {t('products_column_label')}
                      </label>
                      <ProductLabelButton
                        approvalNumber={row.approvalNumber}
                        tooltip={t('products_label_tooltip')}
                        size={16}
                        buttonSize={34}
                      />
                    </div>
                  )}

                  {/* Registered Dosages - on its own line with more spacing */}
                  {row.id && (
                    <div className="w-full mt-2">
                      <label className="block text-xs font-medium text-[#595959] mb-1">
                        {t('products_column_dosages')}
                      </label>
                      <div
                        className="text-sm italic p-2 bg-white rounded-lg border border-gray-200"
                        style={{
                          color: '#8C8C8C',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {row.sageDoses && row.sageDoses.length > 0
                          ? row.sageDoses.join(' • ')
                          : t('products_no_dosage')}
                      </div>
                    </div>
                  )}

                  {/* Dose - on its own line */}
                  <div className="w-full">
                    <label className="block text-xs font-medium text-[#595959] mb-1">
                      {t('products_column_dose')}
                    </label>
                    <DoseInput
                      value={row.dose}
                      onChange={(value) => handleDoseChange(index, value)}
                      placeholder={t('products_dose_placeholder')}
                      disabled={!row.id}
                    />
                  </div>

                  {/* Unit - on its own line */}
                  <div className="w-full">
                    <label className="block text-xs font-medium text-[#595959] mb-1">
                      {t('products_column_unit')}
                    </label>
                    <UnifiedCascadingSelect
                      type="units"
                      cultures={cascadingProps.cultures}
                      treatmentType={cascadingProps.treatmentType}
                      applicationTechnique={cascadingProps.applicationTechnique}
                      value={row.unit || null}
                      onChange={(value, option) =>
                        handleUnitChange(index, value, option)
                      }
                      placeholder={t('products_unit_placeholder')}
                      disabled={!row.id}
                      size="large"
                      enrichedLabel={row.unitName}
                      onMismatchChange={createMismatchHandler(index, 'unit')}
                      allowShowAll
                    />
                  </div>

                  {/* Delete Button */}
                  {!isRowEmpty(row) && (
                    <div className="flex justify-end mt-2">
                      <DeleteRowButton
                        onDelete={() => handleDeleteRow(index)}
                        tooltip={t('products_delete_tooltip')}
                        confirmTitle={t('products_delete_confirm_title')}
                        confirmDescription={t(
                          'products_delete_confirm_description'
                        )}
                        confirmOk={t('products_delete_confirm_ok')}
                        confirmCancel={t('products_delete_confirm_cancel')}
                      />
                    </div>
                  )}
                </div>

                {/* Desktop Layout - Grid */}
                <div className="hidden lg:grid lg:grid-cols-[minmax(200px,2fr)_80px_minmax(140px,2fr)_140px_140px_60px] gap-2 items-start">
                  {/* Product Select */}
                  <div className="min-w-0">
                    <UnifiedCascadingSelect
                      type="products"
                      cultures={cascadingProps.cultures}
                      treatmentType={cascadingProps.treatmentType}
                      applicationTechnique={cascadingProps.applicationTechnique}
                      value={row.id || null}
                      onChange={(value, option) =>
                        handleProductChange(index, value, option)
                      }
                      placeholder={t('products_search_placeholder')}
                      size="large"
                      enrichedLabel={row.name}
                      onMismatchChange={createMismatchHandler(index, 'product')}
                      allowShowAll
                    />
                  </div>

                  {/* Label Icon (Étiquette) */}
                  <div className="flex justify-center">
                    <ProductLabelButton
                      approvalNumber={row.approvalNumber}
                      tooltip={t('products_label_tooltip')}
                    />
                  </div>

                  {/* Registered Dosages */}
                  <div className="flex items-center min-h-[44px] min-w-0">
                    <Input
                      type="text"
                      value={
                        row.sageDoses && row.sageDoses.length > 0
                          ? row.sageDoses.join(' • ')
                          : t('products_no_dosage')
                      }
                      disabled
                      className="h-11 w-full font-normal !text-black"
                      size="large"
                    />

                    {/*{row.id && (*/}
                    {/*  <div*/}
                    {/*    className="text-sm italic"*/}
                    {/*    style={{*/}
                    {/*      color: '#8C8C8C',*/}
                    {/*      wordWrap: 'break-word',*/}
                    {/*      whiteSpace: 'normal',*/}
                    {/*    }}*/}
                    {/*  >*/}
                    {/*    {row.sageDoses && row.sageDoses.length > 0*/}
                    {/*      ? row.sageDoses.join(' • ')*/}
                    {/*      : t('products_no_dosage')}*/}
                    {/*  </div>*/}
                    {/*)}*/}
                  </div>

                  {/* Applied Dose Input */}
                  <div className="min-w-0">
                    <DoseInput
                      value={row.dose}
                      onChange={(value) => handleDoseChange(index, value)}
                      placeholder={t('products_dose_placeholder')}
                      disabled={!row.id}
                      className="w-full"
                    />
                  </div>

                  {/* Unit Select */}
                  <div className="min-w-0">
                    <UnifiedCascadingSelect
                      type="units"
                      cultures={cascadingProps.cultures}
                      treatmentType={cascadingProps.treatmentType}
                      applicationTechnique={cascadingProps.applicationTechnique}
                      value={row.unit || null}
                      onChange={(value, option) =>
                        handleUnitChange(index, value, option)
                      }
                      placeholder={t('products_unit_placeholder')}
                      disabled={!row.id}
                      size="large"
                      enrichedLabel={row.unitName}
                      onMismatchChange={createMismatchHandler(index, 'unit')}
                      allowShowAll
                    />
                  </div>

                  {/* Delete Icon */}
                  <div className="flex justify-center">
                    {!isRowEmpty(row) && (
                      <DeleteRowButton
                        onDelete={() => handleDeleteRow(index)}
                        tooltip={t('products_delete_tooltip')}
                        confirmTitle={t('products_delete_confirm_title')}
                        confirmDescription={t(
                          'products_delete_confirm_description'
                        )}
                        confirmOk={t('products_delete_confirm_ok')}
                        confirmCancel={t('products_delete_confirm_cancel')}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Tank Mix Question - Show after first product if it has a valid product */}
              {index === 0 && !isRowEmpty(row) && (
                <div className="w-full mt-4 lg:mt-0">
                  <p
                    className="text-sm sm:text-base font-bold leading-[125%]"
                    style={{
                      color: 'var(--Neon-9, #105235)',
                      fontFamily: 'Roboto',
                    }}
                  >
                    {t('products_tank_mix_question')}
                  </p>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }
);

Step4CommercialProducts.displayName = 'Step4CommercialProducts';

export default Step4CommercialProducts;
